<template>
	<div>
		<PageHeader
			:data="{
				name: 'Missing Municipalities',
				breadcrumbs: [{ name: 'Home', to: '/' }],
			}"
		/>
		<router-link class="btn btn-primary mb-2" :to="'/municipalities'">
			🏙️ Municipalities
		</router-link>
		<router-link class="btn btn-primary mb-2 ms-2" :to="'/contacts'">
			📙 Municipalities Contacts
		</router-link>
		<router-link class="btn btn-primary mb-2 ms-2" :to="'/municipality-map'">
			📍 Map
		</router-link>
		<router-link class="btn btn-primary mb-2 ms-2 active" :to="'/municipalities-missing'">
			Municipalities Missing
		</router-link>
		<router-link class="btn btn-primary mb-2 ms-2 active" :to="'/municipality-map/polygons'">
			📐 Map Polygons
		</router-link>

		<div v-if="!error" class="row mb-3">
			<!-- Filters -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2"><font-awesome-icon :icon="['fas', 'filter']" class="text-primary me-1" /> Filters</h4>
								<button v-if="Object.values(this.params).length > 4" @click="clearFilters()" class="btn btn-sm btn-danger text-white me-2">
									Clear
									<font-awesome-icon :icon="['fas', 'times']" />
								</button>
							</div>
							<div class="action-buttons"></div>
						</div>

						<LPDataTableFilters :filters="table.filters" v-on:getFilters="getFilters($event.filters)" />
					</div>
				</div>
			</div>
			<!-- Table data -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2 text-capitalize">{{ model }}</h4>
							</div>

							<div class="action-buttons"></div>
						</div>
						<table :class="!loading && tableData.length > 0 ? 'table table-hover' : 'table table-hover loading'">
							<LPDataTableThead :thead="table.columns" :userColumns="userColumns" v-on:setOrder="setOrderBy($event.orderBy, $event.direction)" />
							<LPDataTableTbody
								:userColumns="userColumns"
								:authUser="authUser"
								:data="tableData"
								:tbody="table.columns"
								:model="model"
								:route="route"
								:rowInfo="true"
								v-on:showInformationModal="showInformationModal($event)"
							/>
						</table>
						<div v-if="!loading && tableData.length === 0" class="col-md-12 text-center">
							<h4>No data available</h4>
						</div>
						<div v-if="loading && tableData.length === 0" class="d-flex justify-content-center">
							<div class="spinner-border" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
						<div class="table-actions d-flex">
							<button data-bs-toggle="modal" data-bs-target="#exportModal" class="btn btn-success btn-sm me-2">
								Export <font-awesome-icon :icon="['fas', 'file-csv']" class="me-1" />
							</button>
						</div>
						<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
					</div>
				</div>
			</div>
		</div>

		<div v-else class="row mb-3">
			<div class="col-12">
				<div class="card">
					<div class="card-body">
						<div class="alert alert-danger" role="alert">
							{{ error }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for export -->
		<div class="modal fade" id="exportModal" tabindex="-1" aria-labelledby="exportModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="exampleModalLabel">Export {{ route }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="form-group mb-2">
							<label for="">Export Type</label>
							<select class="form-select" v-model="newExport.type">
								<option value="csv">CSV</option>
							</select>
						</div>
						<div class="form-group mb-2">
							<label for="">File Name</label>
							<div class="input-group mb-3">
								<input type="text" class="form-control" placeholder="File name" v-model="newExport.filename" />
								<span class="input-group-text" id="basic-addon2">.{{ newExport.type }}</span>
							</div>
						</div>
						<div class="form-group">
							<label for="">Columns to export</label>
							<div class="form-check">
								<input class="form-check-input" type="radio" v-model="newExport.columnsType" value="visible" id="visibleColumnsRadio" checked />
								<label class="form-check-label" for="visibleColumnsRadio">
									Export visible columns
								</label>
							</div>
							<div class="my-2" v-if="newExport.columnsType === 'visible'">
								<div class="d-flex flex-wrap">
									<div :key="'column-available-' + column" v-for="column in newExport.exportColumns" class="mb-2 me-1">
										<span class="badge bg-success">{{ column }}</span>
									</div>
								</div>
							</div>

							<div class="my-2" v-if="newExport.columnsType === 'custom'">
								<div class="d-flex flex-wrap">
									<small class="w-100 mb-1">ℹ️ Click on column to include/exclude it from export</small>
									<div :key="'column-available-' + column" v-for="column in newExport.availableColumns" class="mb-2 me-1">
										<span role="button" @click="toggleExportColumn(column)" v-if="newExport.exportColumns.includes(column)" class="badge bg-success">{{
											column
										}}</span>
										<span role="button" @click="toggleExportColumn(column)" v-else class="badge bg-secondary">{{ column }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						<button v-if="!loadExporting && exportingFile === ''" @click="exportData()" type="submit" class="btn btn-primary">
							Create Export
						</button>
						<a v-if="!loadExporting && exportingFile !== ''" @click="downloadExport()" :href="exportingFile" class="btn btn-primary" download>
							Download
						</a>

						<button v-if="loadExporting" class="btn btn-primary" type="button" disabled>
							<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
							Loading...
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { Modal } from 'bootstrap'
import { handleErrors } from '../../actions/helpers.js'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
import LPDataTableFilters from '@/components/lp-data-table/LPDataTableFilters'
import LPDataTableThead from '@/components/lp-data-table/LPDataTableThead'
import LPDataTableTbody from '@/components/lp-data-table/LPDataTableTbody'

export default {
	name: 'MunicipalitiesMissing',
	components: {
		PageHeader,
		LPDataTablePagination,
		LPDataTableFilters,
		LPDataTableThead,
		LPDataTableTbody,
	},
	data() {
		return {
			route: 'municipalities-missing',
			model: 'municipalityMissing',
			tableData: [],
			loading: false,
			error: '',
			newTableEntry: {},
			$createModal: null,
			$exportModal: null,
			$campaignModal: null,
			$carnivoreModal: null,
			$addFilterModal: null,
			pagination: {},
			page: this.$route.query.page || 1,
			orderBy: this.$route.query.orderBy || 'id',
			direction: this.$route.query.direction || 'asc',
			userPerPage: localStorage.getItem('municipality-per-page') || 20,
			table: {
				columns: [
					{
						name: 'id',
						label: 'ID',
						sort: true,
						type: 'default',
					},

					{
						name: 'fips',
						label: 'FIPS',
						sort: false,
						type: 'google',
					},
					{
						name: 'fips_full',
						label: 'FIPS Full',
						sort: false,
						type: 'google',
					},
					{
						name: 'name',
						label: 'Name',
						sort: true,
						type: 'default',
					},
					{
						name: 'state_fips',
						label: 'State FIPS',
						sort: false,
						type: 'default',
					},
					{
						name: 'county_fips',
						label: 'County FIPS',
						sort: false,
						type: 'default',
					},
					{
						name: 'place_fips',
						label: 'Place FIPS',
						sort: false,
						type: 'default',
					},
					{
						name: 'type',
						label: 'Type',
						sort: true,
						type: 'default',
					},

					{
						name: 'state',
						label: 'State',
						sort: true,
						type: 'default',
					},
					{
						name: 'county',
						label: 'County',
						sort: false,
						type: 'default',
					},
					{
						name: 'city',
						label: 'City',
						sort: false,
						type: 'default',
					},
					{
						name: 'zip',
						label: 'ZIP',
						sort: false,
						type: 'default',
					},

					{
						name: 'address',
						label: 'Address',
						sort: false,
						type: 'default',
					},
					{
						name: 'email',
						label: 'Email',
						sort: false,
						type: 'default',
					},
					{
						name: 'population',
						label: 'Population',
						sort: true,
						type: 'default',
					},

					{
						name: 'website',
						label: 'Website',
						sort: false,
						type: 'link',
					},
					{
						name: 'wikipedia',
						label: 'Wikipedia',
						sort: false,
						type: 'link',
					},
					{
						name: 'created_at',
						label: 'Created',
						sort: false,
						type: 'default',
					},
					{
						name: 'updated_at',
						label: 'Updated',
						sort: false,
						type: 'default',
					},
					{
						name: 'actions',
						label: 'Actions',
						sort: false,
						type: 'actions',
						width: 100,
						buttons: [
							{
								type: 'default',
								label: 'View',
								action: '',
								class: 'btn-secondary',
								permission: 'MUNICIPALITY_ACCESS',
							},
							{
								type: 'default',
								label: 'Edit',
								action: '',
								class: 'btn-primary',
								permission: 'MUNICIPALITY_UPDATE',
							},
							{
								type: 'default',
								label: 'Delete',
								action: '',
								class: 'btn-danger',
								permission: 'MUNICIPALITY_DELETE',
							},
						],
					},
				],
				filters: [
					{
						display: true,
						name: 'search',
						label: 'Search',
						type: 'text',
						placeholder: 'enter text and press enter',
						colClass: 'col-3',
						value: this.$route.query.search || '',
					},
					{
						display: true,
						name: 'type',
						label: 'Type',
						type: 'select',
						placeholder: 'All types',
						colClass: 'col-2',
						value: this.$route.query.type || '',
						options: [
							{
								id: 'Town',
								name: 'Town',
							},
							{
								id: 'City',
								name: 'City',
							},
							{
								id: 'Township',
								name: 'Township',
							},
							{
								id: 'Village',
								name: 'Village',
							},
							{
								id: 'Borough',
								name: 'Borough',
							},
						],
					},
					{
						display: true,
						name: 'state',
						label: 'State',
						type: 'multiselect',
						placeholder: 'All states',
						colClass: 'col-3',
						options: [],
						optionValue: true,
						optionsEndpoint: `municipalities/states`,
						value: this.$route.query.state || [],
					},
					{
						display: true,
						name: 'county',
						label: 'County',
						type: 'multiselect',
						placeholder: 'All counties',
						colClass: 'col-4',
						options: [],
						value: this.$route.query.county || [],
					},
					{
						display: true,
						name: 'population',
						label: 'Population',
						type: 'range',
						placeholder: '',
						colClass: 'col-3',
						min: 0,
						max: 99999999,
						from: this.$route.query.frompopulation || '',
						to: this.$route.query.topopulation || '',
					},
				],
			},
			params: {},
			userColumns: ['id', 'name', 'fips_full', 'fips', 'type', 'state', 'county', 'address', 'email', 'website', 'wikipedia', 'population', 'zip'],
			availableColumns: ['id'],
			searchName: '',
			searchSaving: false,
			searches: [],
			newExport: {
				type: 'csv',
				columnsType: 'visible',
				filename: '',
				availableColumns: ['id'],
				exportColumns: ['id', 'name', 'fips_full', 'fips', 'type', 'state', 'county', 'address', 'email', 'website', 'wikipedia', 'population', 'zip'],
			},
			loadExporting: false,
			exportingFile: '',
			$dataInfoModal: null,
			dataInfo: {},
			loadingCampaignObjects: false,
			loadingCampaingCreate: false,
			contactTableKey: 1,
			creatingCarnivoreLoader: false,
			carnivoreType: 'filters',
			customMunicipalities: [],
			addCustomMunicipalities: false,
			newMunicipality: {
				full_name: '',
			},
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
	},
	created() {
		this.table.filters.forEach(filter => {
			if (filter.value && filter.value !== '' && filter.value.length > 0) {
				this.params[filter.name] = filter.value
			} else {
				delete this.params[filter.name]
			}

			if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
				this.params['from' + filter.name] = filter.from
				this.params['to' + filter.name] = filter.to
			} else {
				delete this.params['from' + filter.name]
				delete this.params['to' + filter.name]
			}
		})
		this.getCountyOptions()
		this.getTableColumns()
		this.getTableData(true)
	},
	mounted() {
		this.$createModal = new Modal('#create')
		this.$addFilterModal = new Modal('#addFilter')
		this.$exportModal = new Modal('#exportModal')
		this.$dataInfoModal = new Modal('#dataInfoModal')
		this.$campaignModal = new Modal('#campaignModal')
		this.$carnivoreModal = new Modal('#carnivoreModal')
	},
	methods: {
		// Get table Columns
		getTableColumns() {
			restApi
				.post('/helper/table-columns', { table: this.route })
				.then(({ data }) => {
					this.newExport.availableColumns = data.map(column => column.name)
					this.availableColumns = data.map(column => column.name)
					this.availableColumns.push('actions')
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
		// Get table data from API
		getTableData(first = false) {
			this.loading = true

			this.params = {
				...this.params,
				page: this.page,
				orderBy: this.orderBy,
				direction: this.direction,
				perPage: localStorage.getItem(`${this.model}-per-page`) || 20,
			}

			restApi
				.get(`/${this.route}`, { params: this.params })
				.then(({ data }) => {
					this.tableData = data.data
					this.pagination = data
					delete this.pagination.data
					this.loading = false
					if (!first) {
						this.$router.push({ path: this.$router.currentPath, query: this.params })
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Adding new filter
		addFilter() {
			this.$addFilterModal.hide()
		},

		// Go to user page
		viewUser(user) {
			this.$router.push('/users/' + user.id)
		},

		// Get items from page that returned from LPDataTablePagination
		getItems(page) {
			this.page = page
			this.getTableData()
		},

		// Set direction
		setOrderBy(orderBy, direction) {
			this.orderBy = orderBy
			this.direction = direction
			this.getTableData()
		},

		// Get filters from LPDataTableFilters component
		getFilters(filters) {
			this.page = ''
			filters.forEach(filter => {
				if (filter.value && filter.value !== '' && filter.value.length > 0) {
					this.params[filter.name] = filter.value
				} else {
					delete this.params[filter.name]
				}

				// Setting filter if its type is range
				if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
					this.params['from' + filter.name] = filter.from
					this.params['to' + filter.name] = filter.to
				} else {
					delete this.params['from' + filter.name]
					delete this.params['to' + filter.name]
				}
			})
			this.getCountyOptions()
			this.getTableData()
		},

		// Enable saving if some filters are set
		haveParamsToSave() {
			if (Object.values(this.params).length > 4) {
				return true
			} else {
				return false
			}
		},

		// Clear all filters
		clearFilters() {
			this.params = {}
			this.table.filters.forEach(filter => {
				if (filter.type !== 'range' && filter.type !== 'multiselect') {
					filter.value = ''
				}
				if (filter.type === 'multiselect') {
					filter.value = []
				}
				if (filter.type === 'range' || filter.type === 'daterange') {
					filter.from = ''
					filter.to = ''
				}
			})
			this.getCountyOptions()
			this.getTableData()
		},

		// Toggle export column
		toggleExportColumn(column) {
			if (this.newExport.exportColumns.includes(column)) {
				this.newExport.exportColumns = this.newExport.exportColumns.filter(c => c !== column)
			} else {
				this.newExport.exportColumns.push(column)
			}
		},

		// Export table Data
		exportData() {
			this.loadExporting = true
			this.newExport.exportColumns = this.newExport.exportColumns.filter(column => column !== 'actions')
			restApi
				.post(`/${this.route}/export`, {
					params: JSON.stringify(this.params),
					columns: JSON.stringify(this.newExport.exportColumns),
					filename: this.newExport.filename,
				})
				.then(({ data }) => {
					this.loadExporting = false
					this.exportingFile = this.storageUrl + '/tmp/' + data.file
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Download exported Data
		downloadExport() {
			this.$exportModal.hide()
			this.newExport.filename = ''
			this.exportingFile = ''
		},

		// Set user per page
		setUserPerPage() {
			localStorage.setItem(`${this.model}-per-page`, this.userPerPage)
			this.getTableData()
		},

		// Get county options for selected state
		getCountyOptions() {
			if (this.params.state) {
				restApi.post('municipalities/counties', { state: this.params.state }).then(({ data }) => {
					data = data.filter(county => county !== null)
					this.counties = data
					let filter = this.table.filters.find(f => f.name === 'county')
					filter.options = data
				})
			} else {
				let filter = this.table.filters.find(f => f.name === 'county')
				filter.options = []
				filter.disabled = true
			}
		},
	},
}
</script>
